import { useEffect, useState } from 'react';
import {
	Modal,
	Form,
	Input,
	Button,
	Select,
	InputNumber,
	Row,
	Col,
	Checkbox,
	Table,
	Popconfirm,
	Typography,
	notification,
} from 'antd';
import Fraction from 'fraction.js';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import {
	IVendorMaterial,
	IVendorMaterialLength,
} from '../../interfaces/Material.Interface';
import {
	useCreateVendorMaterialMutation,
	useUpdateVendorMaterialMutation,
	useDeleteVendorMaterialLengthMutation,
} from '../../redux/api/vendor-materials';
import { useDispatch, useSelector } from 'react-redux';
import { closeAddVendorMaterialModal } from '../../redux/slices/material';
import {
	useGetMaterialVendorsQuery,
	useGetMaterialShapesQuery,
	useGetMaterialTypesQuery,
	useGetMaterialCategoriesQuery,
} from '../../redux/api/material';
import {
	ANGLE,
	CHANNEL,
	PLATE,
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
	HEX_BAR,
} from '../../config/constants';
import { RootState } from '../../redux/store';
import { getEffectiveShapeName } from '../../utils/shapeUtils';

interface VendorMaterialModalProps {
	vendorMaterial?: IVendorMaterial | null;
	refetchData: () => void;
}

function decimalToFraction(decimal) {
	if (isNaN(decimal)) throw new Error('Input must be a number');

	// Convert the decimal to a fraction
	const fraction = new Fraction(decimal);

	// Calculate the whole number part
	const wholeNumber =
		fraction.s * (fraction.n / fraction.d >= 1n ? fraction.n / fraction.d : 0n);

	// Calculate the remainder numerator
	const remainderNumerator = fraction.n % fraction.d;

	// If no remainder, return just the whole number
	if (remainderNumerator === 0n) {
		return `${wholeNumber}`;
	}

	// Reduce the remainder fraction
	const remainderFraction = new Fraction(remainderNumerator, fraction.d);

	// If no whole number part, return just the fraction
	if (wholeNumber === 0n) {
		const sign = fraction.s < 0 ? '-' : '';
		return `${sign}${remainderFraction.n}/${remainderFraction.d}`;
	}

	// Otherwise, format as "whole-remainder/fraction"
	const sign = fraction.s < 0 ? '-' : '';
	return `${sign}${Math.abs(Number(wholeNumber))}-${remainderFraction.n}/${
		remainderFraction.d
	}`;
}

const VendorMaterialModal = (props: VendorMaterialModalProps) => {
	const { addVendorMaterialModalVisible } = useSelector(
		(state: RootState) => state.material
	);

	const dispatch = useDispatch();

	const [form] = Form.useForm();

	const [createVendorMaterial, createVendorMaterialResult] =
		useCreateVendorMaterialMutation();
	const [updateVendorMaterial, updateVendorMaterialResult] =
		useUpdateVendorMaterialMutation();
	const [deleteVendorMaterialLength] = useDeleteVendorMaterialLengthMutation();

	const [loading, setLoading] = useState(false);
	const [lengths, setLengths] = useState<IVendorMaterialLength[]>(
		props.vendorMaterial?.lengths ? props.vendorMaterial.lengths : [{
			id: '1',
			length: 144,
			price: 0,
		}]
	);

	const [selectedShape, setSelectedShape] = useState<string | null | undefined>(
		null
	);
	const [selectedCategory, setSelectedCategory] = useState<string>('');

	const vendorsQuery = useGetMaterialVendorsQuery();
	const shapesQuery = useGetMaterialShapesQuery();
	const categoriesQuery = useGetMaterialCategoriesQuery();
	const typesQuery = useGetMaterialTypesQuery(
		{
			material_category: selectedCategory,
		},
		{ skip: selectedCategory === '' }
	);

	function onClose() {
		form.resetFields();
		setSelectedShape(null);
		setLengths([{
			id: '1',
			length: 144,
			price: 0,
		}]);
		dispatch(closeAddVendorMaterialModal());
	}

	const handleOk = async () => {
		try {
			const values = await form.validateFields();

			setLoading(true);

			const payload: Partial<IVendorMaterial> | null = {
				availability_status: values.availability_status,
				vendor: values.vendor,
				shape: values.shape,
				type: values.type,
				width: values.width,
				width_fraction: values.width_fraction,
				height: values.height,
				height_fraction: values.height_fraction,
				outer_diameter: values.outer_diameter,
				outer_diameter_fraction: values.outer_diameter_fraction,
				inner_diameter: values.inner_diameter,
				inner_diameter_fraction: values.inner_diameter_fraction,
				vendor_part_number: values.vendor_part_number,
				vendor_description: values.vendor_description,
				wall_thickness: values.wall_thickness,
				is_standard_size: values.is_standard_size,
				lengths: [],
			};

			if (lengths.length === 0) {
				notification.error({
					message: 'Please add at least one length',
				});
				return;
			}

			const lengthsData: IVendorMaterialLength[] = [];
			lengths.map((length) => {
				lengthsData.push({
					id: length.id || '',
					length: length.length,
					price: length.price,
				});
			});

			if (lengthsData.length > 0 && payload) {
				payload.lengths = lengthsData;
			}

			if (props.vendorMaterial) {
				await updateVendorMaterial({
					id: props.vendorMaterial.id,
					...payload,
				}).then((response: any) => {
					if (response.error) {
						if (response.error.status === 400) {
							notification.error({
								message: 'Error Updating Vendor Material',
								description: response.error.data,
							});
						}
					} else {
						notification.success({
							message: 'Vendor Material Updated',
						});
						props.refetchData();
					}
				});
			} else if (payload) {
				await createVendorMaterial(payload).then((response: any) => {
					if (response.error) {
						if (response.error.status === 400) {
							notification.error({
								message: 'Error Creating Vendor Material',
								description: response.error.data,
							});
						}
					} else {
						notification.success({
							message: 'Vendor Material Created',
							placement: 'topRight',
						});
						props.refetchData();
					}
				});
			}
		} catch (error) {
			console.error('Failed to save VendorMaterial:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	// Populate form with existing vendor material data if passed in
	useEffect(() => {
		if (props.vendorMaterial) {
			form.setFieldsValue({
				vendor: props.vendorMaterial.vendor?.id,
				shape: props.vendorMaterial.shape?.id,
				category: props.vendorMaterial.type.material_category?.id,
				type: props.vendorMaterial.type?.id,
				width: props.vendorMaterial.width,
				width_fraction: props.vendorMaterial.width_fraction,
				height: props.vendorMaterial.height,
				height_fraction: props.vendorMaterial.height_fraction,
				outer_diameter: props.vendorMaterial.outer_diameter,
				outer_diameter_fraction: props.vendorMaterial.outer_diameter_fraction,
				inner_diameter: props.vendorMaterial.inner_diameter,
				inner_diameter_fraction: props.vendorMaterial.inner_diameter_fraction,
				vendor_part_number: props.vendorMaterial.vendor_part_number,
				vendor_description: props.vendorMaterial.vendor_description,
				wall_thickness: props.vendorMaterial.wall_thickness,
				is_standard_size: props.vendorMaterial.is_standard_size,
				availability_status: props.vendorMaterial.availability_status,
			});
			setSelectedShape(
				getEffectiveShapeName(props.vendorMaterial.shape?.display_name)
			);
			setLengths(props.vendorMaterial.lengths || []);
		} else {
			form.resetFields();
			setSelectedShape(null);

		}
	}, [props.vendorMaterial, addVendorMaterialModalVisible]);

	// Handle shape change
	const handleShapeChange = (value: string) => {
		setSelectedShape(
			getEffectiveShapeName(
				shapesQuery?.data?.find((x) => x.id === value)?.display_name
			)
		);
		form.resetFields([
			'width',
			'height',
			'outer_diameter',
			'inner_diameter',
			'wall_thickness',
		]);
	};

	const handleDeleteLength = async (id: string) => {
		try {
			setLoading(true);
			deleteVendorMaterialLength({ id: id })
				.unwrap()
				.then(() => {
					setLengths((prevLengths) =>
						prevLengths.filter((length) => length.id !== id)
					);
				});
		} catch (error) {
			console.error('Failed to delete VendorMaterialLength:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleAddLength = () => {
		const newLength: IVendorMaterialLength = {
			id: String(lengths.length + 1),
			length: 144,
			price: 0,
		};
		setLengths([...lengths, newLength]);
	};

	const handleUpdateLength = (id: string, key: string, value: any) => {
		const newLengths = lengths.map((length) => {
			if (length.id === id) {
				return { ...length, [key]: value };
			}
			return length;
		});
		setLengths(newLengths);
	};

	useEffect(() => {
		if (
			createVendorMaterialResult.isSuccess ||
			updateVendorMaterialResult.isSuccess
		) {
			form.resetFields();
			onClose();
		}
	}, [createVendorMaterialResult, updateVendorMaterialResult]);

	const lengthColumns = [
		{
			title: 'Length',
			key: 'length',
			render: (record: IVendorMaterialLength) => (
				<Form.Item
					name={`lengths_${record.id}_length`}
					initialValue={record.length || 1}
					noStyle
					rules={[{ required: true, message: 'Please input the length!' }]}
				>
					<InputNumber
						min={1}
						onChange={(value) => {
							if (record.id) handleUpdateLength(record.id, 'length', value);
						}}
						style={{ width: '100%' }}
						suffix="inches"
					/>
				</Form.Item>
			),
			visible: true,
		},
		{
			title: 'Price',
			dataIndex: 'price',
			key: 'price',
			render: (_: string, record: IVendorMaterialLength) => (
				<Form.Item
					name={`lengths_${record.id}_price`}
					initialValue={record.price || 0}
					noStyle
				>
					<InputNumber
						min={0}
						precision={3}
						onChange={(value) => {
							if (record.id) handleUpdateLength(record.id, 'price', value);
						}}
						style={{ width: '100%' }}
						placeholder="Price"
					/>
				</Form.Item>
			),
			visible: true,
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_: string, record: IVendorMaterialLength) => (
				<>
					<Popconfirm
						title="Are you sure to delete this length?"
						onConfirm={() => record.id && handleDeleteLength(record.id)}
						okText="Yes"
						cancelText="No"
					>
						<Button
							type="link"
							icon={<CloseOutlined />}
							style={{ color: 'red' }}
						/>
					</Popconfirm>
				</>
			),
			visible: lengths.length > 1,
		},
	].filter((x) => x.visible);

	return (
		<Modal
			open={addVendorMaterialModalVisible}
			title={
				props.vendorMaterial
					? 'Update Vendor Material'
					: 'Add New Vendor Material'
			}
			maskClosable={false}
			width="850px"
			onOk={handleOk}
			onCancel={handleCancel}
			destroyOnClose={true}
			confirmLoading={loading}
			centered={true}
			styles={{
				body: {
					height: '80vh',
					maxHeight: '850px',
					overflowY: 'auto',
					overflowX: 'hidden',
				},
			}}
		>
			<Form form={form} layout="vertical">
				<Row gutter={[8, 0]}>
					<Col span={6}>
						<Form.Item
							name="vendor"
							label="Vendor"
							rules={[{ required: true, message: 'Please select a vendor!' }]}
						>
							<Select
								placeholder="Select a vendor"
								options={vendorsQuery?.data?.map((x) => {
									return { label: x.display_name, value: x.id };
								})}
							/>
						</Form.Item>
					</Col>

					<Col span={6}>
						<Form.Item
							name="shape"
							label="Shape"
							rules={[{ required: true, message: 'Please select a shape!' }]}
						>
							<Select
								placeholder="Select a shape"
								onChange={handleShapeChange}
								options={shapesQuery?.data?.map((x) => {
									return { label: x.display_name, value: x.id };
								})}
							/>
						</Form.Item>
					</Col>

					<Col span={6}>
						<Form.Item
							name="category"
							label="Category"
							rules={[{ required: true, message: 'Please select a category!' }]}
						>
							<Select
								placeholder="Select a category"
								value={selectedCategory}
								onChange={setSelectedCategory}
								options={categoriesQuery?.data?.map((x) => {
									return { label: x.display_name, value: x.id };
								})}
							/>
						</Form.Item>
					</Col>

					<Col span={6}>
						<Form.Item
							name="type"
							label="Type"
							rules={[{ required: true, message: 'Please select a type!' }]}
						>
							<Select
								placeholder="Select a type"
								options={typesQuery?.data?.map((x) => {
									return { label: x.display_name, value: x.id };
								})}
							/>
						</Form.Item>
					</Col>
				</Row>

				{(selectedShape === RECTANGLE_BAR ||
					selectedShape === RECTANGLE_TUBE ||
					selectedShape === PLATE ||
					selectedShape === ANGLE ||
					selectedShape === CHANNEL ||
					selectedShape === HEX_BAR) && (
					<Row gutter={[8, 16]}>
						<Col span={8}>
							<Form.Item
								name="height"
								label="Height"
								rules={[
									{
										required: true,
										message: 'Please input the height!',
									},
								]}
							>
								<InputNumber
									min={0.001}
									max={144}
									placeholder="Height"
									style={{ width: '100%' }}
									onChange={(value) =>
										form.setFieldValue(
											'height_fraction',
											decimalToFraction(Number(value))
										)
									}
									suffix="inches"
								/>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item name="height_fraction" label="Height Fraction">
								<Input placeholder="Height Fraction" />
							</Form.Item>
						</Col>
					</Row>
				)}

				{(selectedShape === RECTANGLE_BAR ||
					selectedShape === RECTANGLE_TUBE ||
					selectedShape === PLATE ||
					selectedShape === ANGLE ||
					selectedShape === CHANNEL) && (
					<Row gutter={[8, 16]}>
						<Col span={8}>
							<Form.Item
								name="width"
								label="Width"
								rules={[
									{
										required: true,
										message: 'Please input the width!',
									},
								]}
							>
								<InputNumber
									min={0.001}
									max={144}
									placeholder="Width"
									style={{ width: '100%' }}
									onChange={(value) =>
										form.setFieldValue(
											'width_fraction',
											decimalToFraction(Number(value))
										)
									}
									suffix="inches"
								/>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item name="width_fraction" label="Width Fraction">
								<Input placeholder="Width Fraction" />
							</Form.Item>
						</Col>
					</Row>
				)}

				{(selectedShape === ROUND_BAR || selectedShape === ROUND_TUBE) && (
					<Row gutter={[8, 16]}>
						<Col span={8}>
							<Form.Item
								name="outer_diameter"
								label="Outer Diameter"
								rules={[
									{
										required: true,
										message: 'Please input the outer diameter!',
									},
								]}
							>
								<InputNumber
									min={0.001}
									max={144}
									placeholder="Outer Diameter"
									style={{ width: '100%' }}
									onChange={(value) =>
										form.setFieldValue(
											'outer_diameter_fraction',
											decimalToFraction(Number(value))
										)
									}
									suffix="inches"
								/>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="outer_diameter_fraction"
								label="Outer Diameter Fraction"
							>
								<Input placeholder="Outer Diameter Fraction" />
							</Form.Item>
						</Col>
					</Row>
				)}

				{selectedShape === ROUND_TUBE && (
					<Row gutter={[8, 16]}>
						<Col span={8}>
							<Form.Item
								name="inner_diameter"
								label="Inner Diameter"
								rules={[
									{
										required: true,
										message: 'Please input the inner diameter!',
									},
								]}
							>
								<InputNumber
									min={0.001}
									max={144}
									placeholder="Inner Diameter"
									style={{ width: '100%' }}
									onChange={(value) =>
										form.setFieldValue(
											'inner_diameter_fraction',
											decimalToFraction(Number(value))
										)
									}
									suffix="inches"
								/>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="inner_diameter_fraction"
								label="Inner Diameter Fraction"
							>
								<Input placeholder="Inner Diameter Fraction" />
							</Form.Item>
						</Col>
					</Row>
				)}

				{(selectedShape === RECTANGLE_TUBE ||
					selectedShape === ANGLE ||
					selectedShape === CHANNEL) && (
					<Row gutter={[8, 16]}>
						<Col span={8}>
							<Form.Item
								name="wall_thickness"
								label="Wall Thickness"
								rules={[
									{
										required: true,
										message: 'Please input the wall thickness!',
									},
								]}
							>
								<InputNumber
									min={0.001}
									max={144}
									placeholder="Wall Thickness"
									style={{ width: '100%' }}
									onChange={(value) =>
										form.setFieldValue(
											'wall_thickness_fraction',
											decimalToFraction(Number(value))
										)
									}
									suffix="inches"
								/>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="wall_thickness_fraction"
								label="Wall Thickness Fraction"
							>
								<Input placeholder="Wall Thickness Fraction" />
							</Form.Item>
						</Col>
					</Row>
				)}

				{/* Vendor Part Number */}
				<Form.Item
					name="vendor_part_number"
					label="Vendor Part Number"
					rules={[
						{
							required: true,
							message: 'Please input the vendor part number!',
						},
					]}
				>
					<Input placeholder="Vendor Part Number" />
				</Form.Item>

				<Form.Item name="vendor_description" label="Vendor Description">
					<Input.TextArea placeholder="Vendor Description" />
				</Form.Item>

				<Row>
					<Col span={8}>
						<Form.Item
							name="availability_status"
							label="Availability Status"
							initialValue={'Available'}
						>
							<Select
								options={[
									{
										value: 'Available',
										label: 'Available',
									},
									{
										value: 'Unavailable',
										label: 'Unavailable',
									},
								]}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item name="is_standard_size" valuePropName="checked">
					<Checkbox>Standard Material</Checkbox>
				</Form.Item>

				<Typography.Title level={5}>
					<span style={{ fontSize: 16, color: 'red' }}>*</span> Available
					Lengths
				</Typography.Title>

				{lengths && (
					<>
						<Table
							dataSource={lengths}
							columns={lengthColumns}
							rowKey="id"
							pagination={false}
							size="small"
						/>
						<Button
							type="dashed"
							onClick={handleAddLength}
							icon={<PlusOutlined />}
							style={{ width: '100%', marginTop: 16 }}
						>
							Add Length
						</Button>
					</>
				)}
			</Form>
		</Modal>
	);
};

export default VendorMaterialModal;
